import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { Plus, Edit, Trash2, Search, Eye, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext'; // Add useNotification
import DeleteConfirmationModal from '../../components/admin/DeleteConfirmationModal'; // New import

const ArticlesManager = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification(); // Destructure addNotification
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Delete Confirmation State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/articles.php`);
      // Safety check: Ensure data is an array
      if (Array.isArray(res.data)) {
        setArticles(res.data);
      } else {
        console.error("Format data salah:", res.data);
        setArticles([]); 
      }
    } catch (err) {
      console.error(err);
      addNotification('Gagal memuat artikel.', 'error'); // Consistent notification
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Delete Modal
  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  // Actual Delete Action
  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/articles.php?id=${deleteTargetId}`);
      setArticles(articles.filter(a => a.id !== deleteTargetId));
      addNotification('Artikel berhasil dihapus', 'success');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } catch (err) {
      addNotification('Gagal menghapus artikel', 'error'); // Consistent notification
    } finally {
      setDeleting(false);
    }
  };

  const styles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    title: { fontSize: '1.5rem', fontWeight: '700' },
    addBtn: { backgroundColor: '#111', color: '#fff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' },
    grid: { display: 'grid', gap: '1rem' },
    card: { backgroundColor: '#fff', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    info: { display: 'flex', gap: '1rem', alignItems: 'center' },
    thumb: { width: '60px', height: '60px', borderRadius: '0.5rem', objectFit: 'cover', backgroundColor: '#f3f4f6' },
    meta: { fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' },
    status: (st) => ({
      padding: '0.25rem 0.75rem', borderRadius: '99px', fontSize: '0.75rem', fontWeight: '600',
      backgroundColor: st === 'published' ? '#d1fae5' : '#f3f4f6',
      color: st === 'published' ? '#065f46' : '#374151'
    }),
    actions: { display: 'flex', gap: '0.5rem' },
    actionBtn: { padding: '0.5rem', borderRadius: '0.4rem', border: 'none', cursor: 'pointer', backgroundColor: '#f9fafb', color: '#374151' }
  };

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <h1 style={styles.title}>Artikel & Blog</h1>
        <button style={styles.addBtn} onClick={() => navigate('/admin/articles/new')}>
          <Plus size={18} /> Tulis Artikel
        </button>
      </div>

      {loading ? <div>Memuat...</div> : (
        <div style={styles.grid}>
          {articles.length === 0 && <div style={{color: '#6b7280'}}>Belum ada artikel.</div>}
          
          {articles.map(art => (
            <div key={art.id} style={styles.card}>
              <div style={styles.info}>
                <img src={art.featured_image || 'https://via.placeholder.com/60'} alt="" style={styles.thumb} />
                <div>
                  <div style={{fontWeight: '700', fontSize: '1rem'}}>{art.title_id}</div>
                  <div style={styles.meta}>
                    <span style={styles.status(art.status)}>{art.status}</span> • Views: {art.views_count} • {new Date(art.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div style={styles.actions}>
                <button style={styles.actionBtn} onClick={() => navigate(`/admin/articles/edit/${art.id}`)}>
                  <Edit size={18} />
                </button>
                <button style={{...styles.actionBtn, color: '#ef4444', backgroundColor: '#fee2e2'}} onClick={() => handleDelete(art.id)}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isConfirming={deleting}
        targetName={articles.find(art => art.id === deleteTargetId)?.title_id || 'artikel ini'}
      />
    </DashboardLayout>
  );
};

export default ArticlesManager;
